import React, { useState } from "react";

import { Grid } from "@material-ui/core";
import { useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";

import {
  Input,
  QuickOptionsWithSelect,
  TextArea,
} from "components/Form/FormikControls";
import {
  getDentitionOptionsByAgeId,
  getDeploymentAgeOptionsByDeploymentIdAndSpeciesId,
  getDeploymentBreedOptionsByDeploymentIdAndSpeciesId,
  getMLAValidatedSectionsBySaleSubTypeId,
  getSaleLotById,
  getSaleSubTypeById,
  getSpeciesAttributes,
  selectScansBySaleLotIdLookup,
} from "selectors";
import { deleteSaleLot } from "actions/offline/saleLots";
import {
  ConfirmDialog,
  createModalTitle,
  MESSAGE_CANT_BE_UNDONE,
} from "components/ConfirmDialog";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import {
  HasDeploymentMarks,
  OtherMarkingField,
  ProductSelectField,
  SaleLotLabelSelectorField,
} from "components/Form/Fields";
import { ForCattle, ForSheep } from "containers/ForSpecies";
import { DentitionField } from "components/Dentition/Index";
import {
  ForClearingSale,
  ForNotClearingSale,
  ForNotHooks,
} from "containers/ForSaleType";
import {
  useHasMLAOrDataPermission,
  useHasSaleyardOrDeploymentPermission,
} from "hooks";
import MarkingField from "components/Form/Fields/MarkingField";
import { AnimalHealthField } from "components/Form/Fields/AnimalHealthField";
import { EIDExemptionReasonField } from "components/Form/Fields/EIDExemptionField";
import {
  DeploymentPermissions,
  SaleyardPermissions,
} from "constants/permissions";
import { SaleType } from "constants/sale";
import { withNamespace } from "components/Form/FormikControls/lib";
import { FormCollapse } from "components/Form";
import { EstAvgWeightRangeField } from "components/WeightRange";
import { DressingRangeField } from "components/DressingRange";
import {
  FrameSectionSimple,
  PregStatusJoiningSection,
  SheepWeightSectionSimple,
} from "components/DetailedSaleLotModal/AdvancedDrafting/Sheep";
import { CattleWeightSectionSimple } from "components/DetailedSaleLotModal/AdvancedDrafting/Cattle";
import { SubtleBadge } from "components/Badge";
import { faTrash } from "@fortawesome/pro-solid-svg-icons";
import { MediaCollapse } from "components/Media/MediaCollapse";
import { IntegrationCollapse } from "components/IntegrationCollapse";
import { ActionsCollapse } from "components/DetailedSaleLotModal/ActionsCollapse";
import { AdvancedDraftingCollapse } from "components/DetailedSaleLotModal/AdvancedDrafting";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import { CurrentSaleReadOnly } from "components/ReadOnly";
import { ApiModel } from "constants/loading";
import { IconTextButton } from "components/Button";

import { MobileTransactionalCreateSaleFormValues } from "components/MobileTransactionalModal/lib/types";
import { FormRow } from "components/MobileTransactionalModal/Tabs/FormRow";

const Sections = {
  GENERAL: "general",
  ADVANCED_DRAFTING: "advancedDrafting",
  MEDIA: "media",
  INTEGRATIONS: "integrations",
  ACTIONS: "actions",
} as const;

interface WhatTabProps {
  deploymentId: number | undefined;
  speciesId: number | null;
  saleLotNamespace: string;
  saleType?: SaleType | null;
  saleSubTypeId?: number | null;
  saleLotId?: string;
  onClose: () => void;
}

export const WhatTab = ({
  deploymentId,
  speciesId,
  saleLotNamespace,
  saleType: saleTypeProp,
  saleSubTypeId: saleSubTypeIdProp,
  saleLotId,
  onClose,
}: WhatTabProps) => {
  const { values, setFieldValue, errors } =
    useFormikContext<MobileTransactionalCreateSaleFormValues>();

  const saleType = saleTypeProp ?? null;
  const saleSubTypeId = saleSubTypeIdProp ?? null;

  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [isConfirmDeleteVisible, setIsConfirmDeleteVisible] = useState(false);

  const dispatch = useDispatch();
  const saleLot = useSelector(getSaleLotById(saleLotId));
  const scansBySaleLotIdLookup = useSelector(selectScansBySaleLotIdLookup);
  const scans = saleLotId ? scansBySaleLotIdLookup[saleLotId] || [] : [];

  const handleDeleteSaleLot = () => {
    if (!saleLot) {
      return;
    }
    dispatch(deleteSaleLot(saleLot));
    setIsConfirmDeleteVisible(false);
    onClose();
  };

  const toggleSection = (section: string) => () =>
    setActiveSection(prev => (prev === section ? null : section));

  const {
    saleLot: {
      age_id: ageId,
      product_id: productId,
      sex_id: sexId,
      breed_id: breedId,
      consignment_id: consignmentId,
    },
  } = values;

  const ageOptions = useSelector(
    getDeploymentAgeOptionsByDeploymentIdAndSpeciesId(deploymentId, speciesId),
  );
  const breedOptions = useSelector(
    getDeploymentBreedOptionsByDeploymentIdAndSpeciesId(
      deploymentId,
      speciesId,
    ),
  );
  const { sexes } = useSelector(getSpeciesAttributes);

  const hasWeightRangePerm = useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureWeightRange,
    SaleyardPermissions.featureWeightRange,
  );

  const dentitionOptions = useSelector(getDentitionOptionsByAgeId(ageId));

  const {
    mlaValidateWeightSection,
    mlaValidateJoiningSection,
    mlaValidateFrameSection,
    mlaValidateAge,
  } = useSelector(getMLAValidatedSectionsBySaleSubTypeId(saleSubTypeId));

  const saleSubType = useSelector(getSaleSubTypeById(saleSubTypeId)) || {};
  const areStudFeaturesEnabled = saleSubType.enableStudFeatures;
  const isDentitionEnabled = saleSubType.enableDentition;
  const isWeightRangeAndDressingEnabled =
    saleSubType.enableWeightRangeAndDressing;

  const hasMLAOrDataPermission = useHasMLAOrDataPermission();

  const showWeightAndDressing =
    mlaValidateWeightSection &&
    isWeightRangeAndDressingEnabled &&
    hasWeightRangePerm;

  const selectedSexHasProgeny = Boolean(sexes[sexId]?.hasProgeny);

  const onChangeBreed = (field: string) =>
    setFieldValue(withNamespace(saleLotNamespace, "breed_id"), field, true);

  return (
    <>
      <Grid container item xs={12}>
        <FormRow>
          <Grid item xs={6}>
            <Input
              name={withNamespace(saleLotNamespace, "quantity")}
              label="Head"
              type="number"
              inputMode="numeric"
              required
            />
          </Grid>
        </FormRow>

        <FormRow>
          <Grid item xs={12}>
            <ProductSelectField
              ns={saleLotNamespace}
              deploymentId={deploymentId}
              selectedSpeciesId={speciesId}
              selectedSaleType={saleType}
            />
          </Grid>
        </FormRow>

        {mlaValidateAge && productId && !ageId && (
          <ForSheep selectedSpeciesId={speciesId}>
            <Grid item xs={12}>
              <QuickOptionsWithSelect
                ns={saleLotNamespace}
                name="age_id"
                label="Age"
                options={ageOptions}
                missingMLAFieldsWarningEnabled
              />
            </Grid>
          </ForSheep>
        )}

        <ForCattle selectedSpeciesId={speciesId}>
          <QuickOptionsWithSelect
            ns={saleLotNamespace}
            name="age_id"
            label="Age"
            options={ageOptions}
            missingMLAFieldsWarningEnabled
          />
        </ForCattle>

        {selectedSexHasProgeny && (
          <FormRow>
            <Grid item xs={12}>
              <Input
                type="number"
                label="Progeny Count"
                name={withNamespace(saleLotNamespace, "quantity_progeny")}
              />
            </Grid>
          </FormRow>
        )}

        {isDentitionEnabled && dentitionOptions.length > 0 && (
          <FormRow>
            <Grid item xs={12}>
              <DentitionField
                name={withNamespace(saleLotNamespace, "dentitionId")}
              />
            </Grid>
          </FormRow>
        )}

        <FormRow>
          <Grid item xs={12}>
            <QuickOptionsWithSelect
              ns={saleLotNamespace}
              name="breed_id"
              label="Breed"
              options={breedOptions}
              missingMLAFieldsWarningEnabled
            />
          </Grid>
        </FormRow>
      </Grid>

      <FormCollapse
        isOpen={activeSection === Sections.GENERAL}
        onToggle={toggleSection(Sections.GENERAL)}
        header={<SubtleBadge>General</SubtleBadge>}
      >
        <>
          {areStudFeaturesEnabled && (
            <>
              <FormRow>
                <Grid item xs={12}>
                  <Input
                    label="Tag #"
                    name={withNamespace(
                      saleLotNamespace,
                      "draftingAttributes.tagNumber",
                    )}
                  />
                </Grid>
              </FormRow>
              <FormRow>
                <Grid item xs={12} data-tour="animalName">
                  <TextArea
                    label="Animal Name"
                    name={withNamespace(
                      saleLotNamespace,
                      "draftingAttributes.animalName",
                    )}
                  />
                </Grid>
              </FormRow>
            </>
          )}
          {showWeightAndDressing && (
            <>
              <FormRow>
                <Grid item xs={6} md={12}>
                  <EstAvgWeightRangeField
                    ns={saleLotNamespace}
                    name="estimatedAverageWeightId"
                    saleRoundIdField={withNamespace(
                      saleLotNamespace,
                      "sale_round_id",
                    )}
                    disabled={false}
                    selectedSaleSubType={saleSubType}
                  />
                </Grid>
              </FormRow>
              <FormRow>
                <Grid item xs={6} md={12}>
                  <DressingRangeField
                    ns={saleLotNamespace}
                    name="dressingRangeId"
                    disabled={false}
                  />
                </Grid>
              </FormRow>
            </>
          )}
          {mlaValidateWeightSection && hasMLAOrDataPermission && (
            <>
              <ForSheep selectedSpeciesId={speciesId}>
                <FormRow>
                  <SheepWeightSectionSimple
                    namespace={saleLotNamespace}
                    readOnly={false}
                  />
                </FormRow>
              </ForSheep>
              <ForCattle selectedSpeciesId={speciesId}>
                <FormRow>
                  <CattleWeightSectionSimple
                    namespace={saleLotNamespace}
                    readOnly={false}
                  />
                </FormRow>
              </ForCattle>
            </>
          )}
          {mlaValidateJoiningSection && hasMLAOrDataPermission && (
            <FormRow>
              <PregStatusJoiningSection
                namespace={saleLotNamespace}
                readOnly={false}
              />
            </FormRow>
          )}
          {mlaValidateFrameSection && (
            <FrameSectionSimple namespace={saleLotNamespace} readOnly={false} />
          )}
          <ForNotHooks selectedSaleType={saleType}>
            <HasDeploymentMarks
              deploymentId={deploymentId}
              selectedSpeciesId={speciesId}
            >
              <FormRow>
                <Grid item xs={12}>
                  <MarkingField
                    deploymentId={deploymentId}
                    name={withNamespace(saleLotNamespace, "marks")}
                    selectedSpeciesId={speciesId}
                    disabled={false}
                  />
                </Grid>
              </FormRow>
            </HasDeploymentMarks>
            <FormRow>
              <Grid item xs={12}>
                <OtherMarkingField
                  deploymentId={deploymentId}
                  name={withNamespace(saleLotNamespace, "marks")}
                  disabled={false}
                  selectedSpeciesId={speciesId}
                />
              </Grid>
            </FormRow>
          </ForNotHooks>
          <FormRow>
            <Grid item xs={12}>
              <SaleLotLabelSelectorField
                label="Labels"
                name={withNamespace(saleLotNamespace, "labels")}
                deploymentId={deploymentId}
                selectedSpeciesId={speciesId}
              />
            </Grid>
          </FormRow>
          <FormRow>
            <Grid data-tour="category" item xs={12}>
              <AnimalHealthField
                ns={saleLotNamespace}
                disabled={false}
                isClearable
                selectedBreedId={breedId}
              />
            </Grid>
          </FormRow>
          <ForNotHooks selectedSaleType={saleType}>
            <ForSheep selectedSpeciesId={speciesId}>
              <FormRow>
                <Grid data-tour="eid" item xs={12}>
                  <EIDExemptionReasonField
                    ns={saleLotNamespace}
                    isClearable
                    disabled={false}
                    selectedSpeciesId={speciesId}
                  />
                </Grid>
              </FormRow>
            </ForSheep>
          </ForNotHooks>
          <FormRow>
            <Grid item xs={12} data-tour="notes">
              <TextArea
                label="Sale Lot Notes"
                name={withNamespace(saleLotNamespace, "note")}
                disabled={false}
                rows={4}
              />
            </Grid>
          </FormRow>
          <CurrentSaleReadOnly>
            {!!saleLotId && (
              <IconTextButton
                icon={faTrash}
                color="gray40"
                onClick={() => setIsConfirmDeleteVisible(true)}
                dataTour="deleteSaleLot"
                disabled={false}
              >
                Delete Sale Lot
              </IconTextButton>
            )}
          </CurrentSaleReadOnly>
          <ConfirmDialog
            title={createModalTitle("this Sale Lot")}
            isOpen={isConfirmDeleteVisible}
            messageIcon={scans.length > 0 ? faExclamationTriangle : null}
            message={
              scans.length > 0 ? (
                <>
                  This Sale Lot contains Scans. Are you sure you want to delete
                  it? {MESSAGE_CANT_BE_UNDONE}
                </>
              ) : (
                MESSAGE_CANT_BE_UNDONE
              )
            }
            onCancel={() => setIsConfirmDeleteVisible(false)}
            onDelete={handleDeleteSaleLot}
          />
        </>
      </FormCollapse>

      <ForNotClearingSale>
        <AdvancedDraftingCollapse
          isOpen={activeSection === Sections.ADVANCED_DRAFTING}
          onToggle={toggleSection(Sections.ADVANCED_DRAFTING)}
          saleLot={values.saleLot}
          commonProps={{ errors: errors.saleLot }}
          onChangeBreed={onChangeBreed}
          readOnly={false}
          namespace={saleLotNamespace}
        />
      </ForNotClearingSale>

      {saleLotId && (
        <ForNotHooks>
          <MediaCollapse
            isOpen={activeSection === Sections.MEDIA}
            onToggle={toggleSection(Sections.MEDIA)}
            saleLotId={saleLotId}
            saleType={saleType}
            readOnly={false}
            consignmentId={null}
            namespace={saleLotNamespace}
          />
        </ForNotHooks>
      )}

      {saleLotId && (
        <ForClearingSale>
          <IntegrationCollapse
            isOpen={activeSection === Sections.INTEGRATIONS}
            onToggle={toggleSection(Sections.INTEGRATIONS)}
            consignmentId={consignmentId}
            namespace={saleLotNamespace}
          />
        </ForClearingSale>
      )}

      {saleLotId && (
        <CurrentSaleReadOnly>
          <WaitForSync requiredData={[ApiModel.SALE_LOTS]}>
            <ActionsCollapse
              isOpen={activeSection === Sections.ACTIONS}
              onToggle={toggleSection(Sections.ACTIONS)}
              saleLotId={saleLotId}
              onClose={onClose}
            />
          </WaitForSync>
        </CurrentSaleReadOnly>
      )}
    </>
  );
};
